import { RecordCollection } from '../../app/record-collection.js';
import { Ban, Eye, FileCheck, FileUp } from 'lucide-react';
import {
  Button,
  CardHeader,
  DateRangePicker,
  Combobox,
  FormErrorSummary,
  Input,
  IconButton,
  Modal,
  Select,
  formatCivilDate,
  formatCurrency,
  useSnackbar,
  type CivilDate,
} from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { PageHeader } from '../../app/page-title.js';
import {
  EmptyCard,
  ErrorCard,
  ListFilterBar,
  LoadingCard,
  Pagination,
  RequestAlert,
} from '../../app/data-view.js';
import {
  paymentReportsApi,
  getApiError,
  type PaymentReportInput,
  type PaymentReportPreview,
  type PaymentReportRow,
} from '../../lib/api.js';
const operatorKey = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/gu, '')
    .trim()
    .toLocaleLowerCase('pt-BR');
const rowKey = (row: PaymentReportRow) => JSON.stringify([row.operatorKey, row.externalId]);
export function CompanyPaymentReports({
  canImport,
  canConfigure,
  canCancel,
  unitName,
  today,
}: {
  canImport: boolean;
  canConfigure: boolean;
  canCancel: boolean;
  unitName: string | null;
  today: CivilDate;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const client = useQueryClient();
  const snackbar = useSnackbar();
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [input, setInput] = useState<PaymentReportInput | null>(null);
  const [preview, setPreview] = useState<PaymentReportPreview | null>(null);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [configure, setConfigure] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [cancelId, setCancelId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [previewRange, setPreviewRange] = useState<{
    from: CivilDate | null;
    to: CivilDate | null;
  }>({ from: null, to: null });
  const [previewSearch, setPreviewSearch] = useState('');
  const [previewOffset, setPreviewOffset] = useState(0);
  const [previewSort, setPreviewSort] = useState<'date' | 'gross' | 'operator'>('date');
  const [submissionId, setSubmissionId] = useState(() => crypto.randomUUID());
  const query = useQuery({
    queryKey: ['company', 'payment-reports', search, offset],
    queryFn: () => paymentReportsApi.list(search, offset),
    retry: false,
  });
  async function run(action: () => Promise<void>) {
    if (busy) return;
    setError(undefined);
    setBusy(true);
    try {
      await action();
    } catch (e) {
      setError(getApiError(e));
    } finally {
      setBusy(false);
    }
  }
  async function inspect() {
    await run(async () => {
      if (!file) {
        setError('Escolha o relatório de vendas da Ton ou o modelo preenchido.');
        return;
      }
      const data = await paymentReportsApi.fileInput(file);
      setInput(data);
      setPreview(await paymentReportsApi.preview(data));
      setPreviewOffset(0);
      setSubmissionId(crypto.randomUUID());
    });
  }
  const rows = (preview?.rows ?? [])
    .filter(
      (r) =>
        (!previewRange.from || r.occurredOn >= previewRange.from) &&
        (!previewRange.to || r.occurredOn <= previewRange.to),
    )
    .filter((r) =>
      `${r.operatorName} ${r.externalId} ${r.financeAccountName}`
        .toLocaleLowerCase('pt-BR')
        .includes(previewSearch.toLocaleLowerCase('pt-BR')),
    )
    .sort((a, b) =>
      previewSort === 'gross'
        ? b.grossCents - a.grossCents
        : previewSort === 'operator'
          ? a.operatorName.localeCompare(b.operatorName)
          : a.occurredOn.localeCompare(b.occurredOn),
    );
  function batchActions(batch: NonNullable<typeof query.data>['items'][number]) {
    return (
      <>
        <IconButton label="Ver relatório" icon={<Eye />} onClick={() => setDetailId(batch.id)} />
        {canCancel && !batch.canceledAt ? (
          <IconButton
            label="Cancelar relatório"
            icon={<Ban />}
            onClick={() => {
              setCancelId(batch.id);
              setReason('');
              setError(undefined);
            }}
          />
        ) : null}
      </>
    );
  }
  return (
    <section>
      <PageHeader
        title="Relatórios das operadoras"
        trail="Financeiro / Relatórios das operadoras"
        description="Importe taxas efetivas e confira quando o líquido ficará disponível na conta financeira da operadora."
      />
      {!unitName ? (
        <RequestAlert message="Escolha uma unidade para configurar ou importar relatórios." />
      ) : null}
      <div className="flex flex-wrap gap-3 mb-4">
        {canConfigure ? (
          <Button variant="outline" onClick={() => setConfigure(true)}>
            Configurar operadoras
          </Button>
        ) : null}
        {canImport ? (
          <Button
            variant="outline"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                const blob = await paymentReportsApi.template();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'modelo-relatorio-operadoras.xlsx';
                a.click();
                URL.revokeObjectURL(url);
              })
            }
          >
            Baixar modelo Excel
          </Button>
        ) : null}
      </div>
      {canImport && unitName ? (
        <form
          className="rounded-xl border border-surface-border bg-surface-raised p-4 mb-4 text-sm"
          onSubmit={(e) => {
            e.preventDefault();
            void inspect();
          }}
        >
          <CardHeader title="Importar relatório" icon={<FileUp className="size-4" />} />
          <p className="mb-3">
            Use o relatório de vendas da Ton em .xlsx ou preencha as abas Transações e Repasses do
            modelo. O extrato confirma o dinheiro recebido depois.
          </p>
          <div className="grid gap-2">
            <span className="text-xs font-medium text-content-muted">Arquivo Excel</span>
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => fileInput.current?.click()}
            >
              Selecionar arquivo Excel
            </Button>
            <span className="break-all text-content-muted">
              {file?.name ?? 'Nenhum arquivo selecionado'}
            </span>
            <input
              ref={fileInput}
              aria-label="Arquivo Excel"
              type="file"
              disabled={busy}
              accept=".xlsx"
              className="sr-only"
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                setPreview(null);
                setInput(null);
              }}
            />
          </div>
          <div className="mt-3 flex gap-3">
            <Button type="submit" disabled={busy}>
              Conferir prévia
            </Button>
            <FormErrorSummary />
          </div>
        </form>
      ) : null}
      {error ? <RequestAlert message={error} /> : null}
      {preview && input ? (
        <div className="rounded-xl border border-surface-border bg-surface-raised p-4 mb-6 text-sm">
          <CardHeader title="Prévia da importação" icon={<FileCheck className="size-4" />} />
          <p>
            Confira a receita original, a taxa e os repasses. Importar não confirma depósito. Sem
            vínculo, o relatório não cria receita no DRE.
          </p>
          <ListFilterBar
            search={previewSearch}
            onSearchChange={(value) => {
              setPreviewSearch(value);
              setPreviewOffset(0);
            }}
            searchLabel="Buscar transação"
            count={`${rows.length} ${rows.length === 1 ? 'transação' : 'transações'}`}
            variant="panel"
          />
          <DateRangePicker
            label="Período das transações"
            value={previewRange}
            onValueChange={(range) => {
              setPreviewRange(range);
              setPreviewOffset(0);
            }}
            today={today}
            emptyLabel="Todo o arquivo"
          />
          <p className="my-3">
            A confirmação importa {preview.rows.length}{' '}
            {preview.rows.length === 1 ? 'transação' : 'transações'} do arquivo, inclusive as
            ocultas pelos filtros.
          </p>
          <Select
            label="Ordenar prévia"
            value={previewSort}
            onValueChange={(v) => {
              setPreviewSort(v as typeof previewSort);
              setPreviewOffset(0);
            }}
            options={[
              { value: 'date', label: 'Data crescente' },
              { value: 'gross', label: 'Bruto decrescente' },
              { value: 'operator', label: 'Operadora crescente' },
            ]}
          />
          <div className="grid gap-3 mt-3">
            {rows.slice(previewOffset, previewOffset + 25).map((row) => (
              <article key={rowKey(row)} className="rounded-lg border border-surface-border p-3">
                <h3 className="font-semibold break-all">
                  {row.operatorName} · {row.externalId}
                </h3>
                <p>
                  {formatCivilDate(row.occurredOn)} · Bruto{' '}
                  {formatCurrency(row.grossCents, row.currency)} · Taxa{' '}
                  {formatCurrency(row.feeCents, row.currency)} · Líquido{' '}
                  {formatCurrency(row.netCents, row.currency)}
                </p>
                <p>Conta financeira: {row.financeAccountName}</p>
                <p>
                  Repasses:{' '}
                  {row.payouts
                    .map(
                      (p) =>
                        `${formatCivilDate(p.dueOn)}: ${formatCurrency(p.netCents, row.currency)}`,
                    )
                    .join(' · ')}
                </p>
                {row.duplicate ? (
                  <RequestAlert message="Transação já importada. Retire-a do arquivo antes de confirmar." />
                ) : null}
                <Combobox
                  label="Receita original"
                  placeholder="Escolha a receita"
                  selectedLabel={
                    preview.incomeOptions.find(
                      (o) =>
                        JSON.stringify(o.target) === JSON.stringify(input.targets[rowKey(row)]),
                    )?.description ?? 'Sem vínculo com receita original'
                  }
                  options={[
                    { value: 'null', label: 'Sem vínculo com receita original' },
                    ...preview.incomeOptions
                      .filter((o) => o.currency === row.currency)
                      .map((o) => ({
                        value: JSON.stringify(o.target),
                        label: `${o.description} · saldo bruto ${formatCurrency(o.availableCents, o.currency)}`,
                      })),
                  ]}
                  onSelect={(choice) => {
                    if (choice.kind !== 'option') return;
                    setInput((current) =>
                      current
                        ? {
                            ...current,
                            targets: {
                              ...current.targets,
                              [rowKey(row)]: JSON.parse(choice.value) as PaymentReportRow['target'],
                            },
                          }
                        : null,
                    );
                    setPreview((current) => (current ? { ...current, fingerprint: '' } : null));
                  }}
                />
                {row.proposedTarget && !input.targets[rowKey(row)] ? (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setInput((current) =>
                        current
                          ? {
                              ...current,
                              targets: { ...current.targets, [rowKey(row)]: row.proposedTarget },
                            }
                          : null,
                      );
                      setPreview((current) => (current ? { ...current, fingerprint: '' } : null));
                    }}
                  >
                    Usar sugestão de receita
                  </Button>
                ) : null}
              </article>
            ))}
          </div>
          <Pagination
            offset={previewOffset}
            limit={25}
            total={rows.length}
            onOffsetChange={setPreviewOffset}
            itemLabel={(count) => (count === 1 ? 'transação' : 'transações')}
          />
          <div className="flex flex-wrap gap-3 mt-4">
            <Button
              variant="outline"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  setPreview(await paymentReportsApi.preview(input));
                })
              }
            >
              Atualizar prévia
            </Button>
            <Button
              disabled={busy || !preview.fingerprint || preview.rows.some((r) => r.duplicate)}
              onClick={() =>
                void run(async () => {
                  await paymentReportsApi.confirm({
                    ...input,
                    submissionId,
                    fingerprint: preview.fingerprint,
                  });
                  setPreview(null);
                  setInput(null);
                  setFile(null);
                  await client.invalidateQueries({ queryKey: ['company'] });
                  snackbar.success(
                    'Relatório importado. Concilie os créditos no extrato da conta financeira da operadora.',
                  );
                })
              }
            >
              Confirmar importação
            </Button>
          </div>
          {!preview.fingerprint ? (
            <p className="mt-2">
              Atualize a prévia para conferir os vínculos escolhidos antes de confirmar.
            </p>
          ) : null}
        </div>
      ) : null}
      <ListFilterBar
        search={search}
        onSearchChange={(v) => {
          setSearch(v);
          setOffset(0);
        }}
        searchLabel="Buscar relatório pelo arquivo"
        count={`${query.data?.total ?? 0} relatórios`}
      />
      {query.isLoading ? (
        <LoadingCard label="Carregando relatórios" />
      ) : query.error ? (
        <ErrorCard message={getApiError(query.error)} />
      ) : (
        <div className="grid gap-3">
          {!query.data?.items.length ? (
            <EmptyCard
              icon={FileCheck}
              title="Nenhum relatório encontrado"
              description="Importe o relatório de vendas da Ton ou o modelo Excel preenchido."
            />
          ) : null}
          <RecordCollection
            label="Relatórios das operadoras"
            records={query.data?.items ?? []}
            recordKey={(batch) => batch.id}
            columns={[
              {
                key: 'file',
                label: 'Arquivo',
                value: (batch) => batch.fileName,
                render: (batch) => <strong className="break-all">{batch.fileName}</strong>,
              },
              {
                key: 'source',
                label: 'Origem',
                value: (batch) => batch.source,
                render: (batch) =>
                  batch.source === 'TON_XLSX' ? 'Relatório da Ton' : 'Modelo Excel',
              },
              {
                key: 'status',
                label: 'Situação',
                value: (batch) => Number(Boolean(batch.canceledAt)),
                render: (batch) => (
                  <div>
                    {batch.canceledAt ? 'Cancelado' : 'Importado'}
                    {batch.canceledReason ? (
                      <p className="text-xs text-content-muted">Motivo: {batch.canceledReason}</p>
                    ) : null}
                  </div>
                ),
              },
            ]}
            actions={batchActions}
            card={(batch) => (
              <article key={batch.id} className="rounded-lg border border-surface-border p-4">
                <h2 className="font-semibold break-all">{batch.fileName}</h2>
                <IconButton
                  label="Ver relatório"
                  icon={<Eye />}
                  onClick={() => setDetailId(batch.id)}
                />
                <p>
                  {batch.canceledAt ? 'Cancelado' : 'Importado'} ·{' '}
                  {batch.source === 'TON_XLSX' ? 'Relatório da Ton' : 'Modelo Excel'}
                </p>
                {batch.canceledReason ? <p>Motivo: {batch.canceledReason}</p> : null}
                {canCancel && !batch.canceledAt ? (
                  <IconButton
                    label="Cancelar relatório"
                    icon={<Ban />}
                    onClick={() => {
                      setCancelId(batch.id);
                      setReason('');
                      setError(undefined);
                    }}
                  />
                ) : null}
              </article>
            )}
          />
        </div>
      )}
      <Pagination
        offset={offset}
        limit={25}
        total={query.data?.total ?? 0}
        onOffsetChange={setOffset}
        itemLabel={(count) => (count === 1 ? 'relatório' : 'relatórios')}
      />
      <Modal
        open={detailId !== null}
        onOpenChange={(open) => {
          if (!open) setDetailId(null);
        }}
        title="Relatório importado"
      >
        {detailId ? <ReportDetails id={detailId} today={today} /> : null}
      </Modal>
      <Modal
        open={configure}
        onOpenChange={setConfigure}
        title="Configurar operadoras"
        footer={
          <div className="flex gap-3">
            <Button type="submit" form="operator-report-setting">
              Salvar configuração
            </Button>
            <FormErrorSummary form="operator-report-setting" />
          </div>
        }
      >
        <OperatorSettings unitName={unitName} />
      </Modal>
      <Modal
        open={cancelId !== null}
        onOpenChange={(open) => {
          if (!open) setCancelId(null);
        }}
        title="Cancelar relatório"
        footer={
          <Button
            disabled={busy}
            onClick={() =>
              void run(async () => {
                await paymentReportsApi.cancel(cancelId!, reason);
                setCancelId(null);
                await client.invalidateQueries({ queryKey: ['company'] });
                snackbar.success('Relatório cancelado.');
              })
            }
          >
            Confirmar cancelamento
          </Button>
        }
      >
        <p>Desfaça as conciliações dos repasses antes de cancelar. O histórico será preservado.</p>
        <Input label="Motivo" value={reason} onChange={(e) => setReason(e.target.value)} />
        {error ? <RequestAlert message={error} /> : null}
      </Modal>
    </section>
  );
}
function OperatorSettings({ unitName }: { unitName: string | null }) {
  const query = useQuery({
    queryKey: ['company', 'payment-report-settings'],
    queryFn: () => paymentReportsApi.settings(),
    retry: false,
  });
  const client = useQueryClient();
  const snackbar = useSnackbar();
  const [scope, setScope] = useState<'COMPANY' | 'UNIT'>(unitName ? 'UNIT' : 'COMPANY');
  const [operatorName, setOperatorName] = useState('Ton');
  const [draft, setDraft] = useState<{
    key: string;
    days: string;
    account: string;
    inherit: boolean;
  } | null>(null);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  // Rascunho vale só para a combinação escolhida; trocar operadora ou escopo volta ao salvo.
  const currentOperatorKey = operatorKey(operatorName);
  const draftKey = `${scope}:${currentOperatorKey}`;
  const saved = query.data?.settings.find(
    (r) =>
      r.operatorKey === currentOperatorKey &&
      (scope === 'COMPANY' ? r.businessUnitId === null : r.businessUnitId !== null),
  );
  const values =
    draft?.key === draftKey
      ? draft
      : {
          key: draftKey,
          days: String(saved?.receiptDays ?? (currentOperatorKey === 'ton' ? 0 : '')),
          account: saved?.financeAccountId ?? '',
          inherit: Boolean(saved && saved.receiptDays === null),
        };
  const { days, account, inherit } = values;
  const edit = (patch: Partial<Omit<typeof values, 'key'>>) => setDraft({ ...values, ...patch });
  const setDays = (next: string) => edit({ days: next });
  const setAccount = (next: string) => edit({ account: next });
  const setInherit = (next: boolean) => edit({ inherit: next });
  if (query.isLoading) return <LoadingCard label="Carregando operadoras" />;
  if (query.error) return <ErrorCard message={getApiError(query.error)} />;
  const latest = new Map<string, NonNullable<typeof query.data>['settings'][number]>();
  for (const row of query.data?.settings ?? []) {
    const k = `${row.operatorKey}:${row.businessUnitId ?? 'company'}`;
    if (!latest.has(k)) latest.set(k, row);
  }
  return (
    <form
      id="operator-report-setting"
      onSubmit={(e) => {
        e.preventDefault();
        if (busy) return;
        if ((scope === 'COMPANY' || !inherit) && days.trim() === '') {
          setError('Informe o prazo em dias corridos.');
          return;
        }
        setError(undefined);
        setBusy(true);
        void paymentReportsApi
          .saveSetting({
            operatorName,
            scope,
            receiptDays: scope === 'UNIT' && inherit ? null : Number(days),
            financeAccountId: scope === 'UNIT' ? account || null : null,
          })
          .then(async () => {
            await client.invalidateQueries({ queryKey: ['company', 'payment-report-settings'] });
            setDraft(null);
            snackbar.success('Configuração salva. Relatórios anteriores preservados.');
          })
          .catch((e) => setError(getApiError(e)))
          .finally(() => setBusy(false));
      }}
      className="grid gap-3"
    >
      <p>
        O prazo é contado em dias corridos após a transação. Zero significa disponibilidade prevista
        no mesmo dia. Datas informadas em Repasses prevalecem.
      </p>
      <Select
        label="Configurar"
        value={scope}
        onValueChange={(v) => setScope(v as typeof scope)}
        options={[
          { value: 'COMPANY', label: 'Padrão da empresa' },
          ...(unitName ? [{ value: 'UNIT', label: `Unidade: ${unitName}` }] : []),
        ]}
      />
      <Input
        label="Operadora"
        value={operatorName}
        onChange={(e) => setOperatorName(e.target.value)}
      />
      {scope === 'UNIT' ? (
        <Select
          label="Prazo da unidade"
          value={inherit ? 'INHERIT' : 'CUSTOM'}
          onValueChange={(v) => setInherit(v === 'INHERIT')}
          options={[
            { value: 'CUSTOM', label: 'Definir prazo nesta unidade' },
            { value: 'INHERIT', label: 'Usar prazo padrão da empresa' },
          ]}
        />
      ) : null}
      {scope === 'COMPANY' || !inherit ? (
        <Input
          label="Disponível após quantos dias corridos"
          type="number"
          min={0}
          max={730}
          value={days}
          onChange={(e) => setDays(e.target.value)}
        />
      ) : null}
      {scope === 'UNIT' ? (
        <Combobox
          label="Conta financeira da operadora"
          placeholder="Escolha a conta financeira"
          selectedLabel={query.data?.accounts.find((a) => a.id === account)?.displayName ?? ''}
          onSelect={(choice) => {
            if (choice.kind === 'option') setAccount(choice.value);
          }}
          options={(query.data?.accounts ?? []).map((a) => ({
            value: a.id,
            label: `${a.displayName} · ${a.currency}`,
          }))}
        />
      ) : null}
      {error ? <RequestAlert message={error} /> : null}

      <p className="text-sm">Configurações atuais da operadora informada:</p>
      {[...latest.values()]
        .filter((row) => row.operatorKey.includes(operatorKey(operatorName)))
        .map((row) => (
          <p key={row.id}>
            {row.operatorName} · {row.businessUnitId ? 'Unidade' : 'Empresa'} ·{' '}
            {row.receiptDays === null ? 'Prazo herdado' : `${row.receiptDays} dias corridos`} ·{' '}
            {row.financeAccountId
              ? (query.data?.accounts.find((a) => a.id === row.financeAccountId)?.displayName ??
                'Conta financeira configurada')
              : 'Sem conta financeira nesta configuração'}
          </p>
        ))}
    </form>
  );
}

function ReportDetails({ id, today }: { id: string; today: CivilDate }) {
  const query = useQuery({
    queryKey: ['company', 'payment-report-detail', id],
    queryFn: () => paymentReportsApi.detail(id),
    retry: false,
  });
  const [search, setSearch] = useState('');
  const [range, setRange] = useState<{ from: CivilDate | null; to: CivilDate | null }>({
    from: null,
    to: null,
  });
  const [offset, setOffset] = useState(0);
  if (query.isLoading) return <LoadingCard label="Carregando relatório" />;
  if (query.error) return <ErrorCard message={getApiError(query.error)} />;
  const rows = (query.data?.rows ?? [])
    .filter(
      (r) => (!range.from || r.occurredOn >= range.from) && (!range.to || r.occurredOn <= range.to),
    )
    .filter((r) =>
      `${r.operatorName} ${r.externalId}`
        .toLocaleLowerCase('pt-BR')
        .includes(search.toLocaleLowerCase('pt-BR')),
    );
  return (
    <div>
      <p className="break-all">{query.data?.fileName}</p>
      <ListFilterBar
        variant="panel"
        searchLabel="Buscar no relatório"
        search={search}
        onSearchChange={(v) => {
          setSearch(v);
          setOffset(0);
        }}
        count={`${rows.length} ${rows.length === 1 ? 'transação' : 'transações'}`}
      />
      <DateRangePicker
        label="Período do relatório"
        value={range}
        onValueChange={(r) => {
          setRange(r);
          setOffset(0);
        }}
        today={today}
        emptyLabel="Todo o relatório"
      />
      <div className="grid gap-3">
        {rows.slice(offset, offset + 25).map((row) => (
          <article key={rowKey(row)} className="rounded-lg border border-surface-border p-3">
            <h3 className="font-semibold break-all">
              {row.operatorName} · {row.externalId}
            </h3>
            <p>
              {formatCivilDate(row.occurredOn)} · Bruto{' '}
              {formatCurrency(row.grossCents, row.currency)} · Taxa{' '}
              {formatCurrency(row.feeCents, row.currency)} · Líquido{' '}
              {formatCurrency(row.netCents, row.currency)}
            </p>
            <p>Conta financeira: {row.financeAccountName}</p>
            <p>Receita original: {row.targetDescription ?? 'Sem vínculo com receita original'}</p>
            {row.payouts.map((p) => (
              <p key={p.number}>
                Repasse {p.number} · {formatCivilDate(p.dueOn)} · Previsto{' '}
                {formatCurrency(p.netCents, row.currency)} · Conciliado{' '}
                {formatCurrency(
                  query.data?.payouts.find(
                    (saved) =>
                      saved.operatorKey === row.operatorKey &&
                      saved.externalId === row.externalId &&
                      saved.number === p.number,
                  )?.receivedCents ?? 0,
                  row.currency,
                )}
              </p>
            ))}
          </article>
        ))}
      </div>
      <Pagination
        offset={offset}
        limit={25}
        total={rows.length}
        onOffsetChange={setOffset}
        itemLabel={(n) => (n === 1 ? 'transação' : 'transações')}
      />
    </div>
  );
}
